import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Anchor } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { BRAND, SERVICES, REGIONS, DISCLAIMER } from "@/data/content";

export const Footer = () => {
  return (
    <footer className="bg-navy-900 text-slate-300 border-t border-gold-500/15" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <BrandMark className="h-12 w-12" />
              <div>
                <p className="font-serif text-xl text-white">Hussar Marine Concierge</p>
                <p className="eyebrow text-gold-400">{BRAND.tagline}</p>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
              Independent dockside vessel oversight for absentee and seasonal owners
              across Southwest Florida. A division of {BRAND.parent}.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-2 text-slate-300 hover:text-gold-400 transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4 text-gold-500" /> {BRAND.email}
              </a>
              <p className="flex items-center gap-2 text-slate-400">
                <MapPin className="h-4 w-4 text-gold-500" /> {BRAND.serviceArea}
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="eyebrow text-gold-400 mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-slate-300 hover:text-gold-400 transition-colors">
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/pricing" className="text-slate-300 hover:text-gold-400 transition-colors">
                  Plans & Pricing
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="eyebrow text-gold-400 mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="text-slate-300 hover:text-gold-400 transition-colors">About HMC</Link></li>
              <li><Link to="/contact" className="text-slate-300 hover:text-gold-400 transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="text-slate-300 hover:text-gold-400 transition-colors">Privacy & Terms</Link></li>
              <li><Link to="/request-inspection" className="text-slate-300 hover:text-gold-400 transition-colors">Request Inspection</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="eyebrow text-gold-400 mb-4">Proposed Service Area</h4>
            <div className="flex flex-wrap gap-2">
              {REGIONS.map((r) => (
                <span
                  key={r}
                  className="text-xs text-slate-300 border border-white/10 rounded-full px-3 py-1.5"
                >
                  {r}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500 max-w-sm">
              Coverage is subject to availability and marina authorization at each location.
            </p>
            <Link
              to="/request-inspection"
              className="mt-6 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
              data-testid="footer-cta"
            >
              <Anchor className="h-4 w-4" /> Request an Inspection
            </Link>
          </div>
        </div>

        <div className="gold-rule mt-14 mb-8" />

        <p className="text-xs text-slate-500 leading-relaxed max-w-4xl">{DISCLAIMER}</p>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {BRAND.parent}. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-gold-400 transition-colors">Privacy</Link>
            <Link to="/privacy" className="hover:text-gold-400 transition-colors">Terms</Link>
            <Link to="/admin/login" className="hover:text-gold-400 transition-colors" data-testid="footer-admin-link">
              Owner Login
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
